import { defineStore } from 'pinia'
import { PaymentMethod } from '@webapp/gp/GroupAdminModel'
import { useGroupAdminApi } from './GroupAdminApi'
import { useUserStore } from './UserStore'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { AsyncDataStore } from '@webapp/util/AsyncDataStore'
import { useTokenManager } from '@webapp/auth/TokenManager'
import { assignExisting, isDefined } from '@webapp/util/TypeScriptUtil'
import { addRedactRule } from '@webapp/util/RedactRules'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { DateTime } from 'luxon'
import { uniqBy } from 'lodash'

export interface LocalPaymentMethodData {
    cardNumber: string
    expirationMonth: number
    expirationYear: number
}

addRedactRule({ pattern: new RegExp('^/paymentintents/[^/]+/confirm$'), params: ['clientSecret'] })

export const usePaymentMethodStore = defineStore('paymentMethods', () => {
    const asyncPaymentMethods = new Map<string, AsyncData<PaymentMethod>>()

    const gp = useGroupAdminApi()
    const userStore = useUserStore()
    const tokenManager = useTokenManager()

    function getLoadedPaymentMethod(id: string | undefined) {
        const store = usePaymentMethodStore() // use to get/return reactive store objects
        return store.asyncPaymentMethods.get(id ?? '')?.data
    }

    function getLoadedPaymentMethodList(ids: (string | undefined)[]) {
        const store = usePaymentMethodStore() // use to get/return reactive store objects
        return ids.map(id => store.asyncPaymentMethods.get(id ?? '')).map(a => a?.data).filter(isDefined)
    }

    function getAsyncKeyListForUser(userId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = usePaymentMethodStore() // use to get/return reactive store objects
        const loadAsync = async (userId: string[], requestTime: DateTime) => {
            const path = `users/${userId}/paymentmethods`
            const plainSubs = await gp.getPlainCollectionAsync(path)
            console.log(`Loaded ${plainSubs.length} payment methods at ${path}`)
            return processExpandedPaymentMethods(plainSubs, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListForRelatedItems(tokenManager.current.userId, "User", 
            userStore.asyncPaymentMethodKeys, store.asyncPaymentMethods, "PaymentMethod", loadMode, loadAsync)
    }

    function getAsyncKeyListForSelf(loadMode = LoadMode.TrackOnly) {
        // NOTE: use the actual user id, because we don't want $me as a cache key
        return getAsyncKeyListForUser(tokenManager.current.userId, loadMode)
    }

    async function confirmLocalPayment(data: LocalPaymentMethodData, intentId: string, clientSecret: string) {
        await gp.postPlainAsync(`paymentintents/${intentId}/confirm`, data, { clientSecret })
    }

    function addPaymentMethodsToStore(plainPaymentMethods: any[], requestTime: DateTime) {
        const store = usePaymentMethodStore() // use to get/return reactive store objects
        const paymentMethodMap = processExpandedPaymentMethods(uniqBy(plainPaymentMethods, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncPaymentMethods, "PaymentMethod", paymentMethodMap, requestTime)
        return paymentMethodMap.values()
    }

    function processExpandedPaymentMethods(plainItems: any[], requestTime: DateTime) {
        // no expansion supported (yet)
        return AsyncDataStore.createItemMap(plainItems, "PaymentMethod", plainToPaymentMethod, pm => pm.id!)
    }

    function plainToPaymentMethod(p: any) {
        const pm = assignExisting(new PaymentMethod(), p)
        pm.expirationDate = DateTimeUtil.fromAPI(p.expirationDate)
        pm.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
        return pm        
    }

    return {
        asyncPaymentMethods,

        getLoadedPaymentMethod,
        getLoadedPaymentMethodList,
        getAsyncKeyListForSelf,
        confirmLocalPayment,
        addPaymentMethodsToStore,
    }
})
